/**
 * Confirmation before creating a REPLACEMENT Bexio draft.
 *
 * Separate from BexioDraftCreateConfirmModal on purpose. A first create asks "shall I
 * invoice this month?"; this one asks "the document I made for this month is gone at
 * Bexio — shall I make another?". The second question needs the previous document's
 * identity on screen, otherwise the operator cannot tell a genuine loss from a row
 * they are looking at by mistake.
 *
 * Same safety mechanics as the create modal: focus moves to confirm on open, Escape
 * cancels while idle, focus is restored on unmount, and the confirm button disables
 * itself in flight so one intent cannot become two Bexio invoices.
 */
import React, { useEffect, useRef } from "react";

import { monthLabel } from "./bexioDraftLabels";

export default function BexioDraftReplaceConfirmModal({
  open,
  row,
  year,
  month,
  submitting,
  onCancel,
  onConfirm,
}) {
  const confirmRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    confirmRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previouslyFocused.current?.focus) previouslyFocused.current.focus();
    };
  }, [open, submitting, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bexio-replace-title"
        className="w-full max-w-md rounded-xl border border-input bg-card p-5 shadow-lg"
      >
        <h2 id="bexio-replace-title" className="text-base font-semibold text-foreground">
          Ersatzentwurf erstellen?
        </h2>

        <dl className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Referenz</dt>
            <dd className="text-right text-foreground">{row?.reference || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Abrechnungsmonat</dt>
            <dd className="text-right text-foreground">{monthLabel(year, month)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Bisheriger Bexio-Beleg</dt>
            <dd className="text-right text-foreground">
              {row?.replacedDocumentNr || row?.replacedBexioInvoiceId || "—"}
            </dd>
          </div>
        </dl>

        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          Der bisherige Bexio-Entwurf existiert nicht mehr. Es wird ein NEUER echter
          Rechnungsentwurf in Bexio erstellt. Der Monatsanspruch bleibt derselbe; die
          bisherige Belegnummer bleibt im Verlauf sichtbar.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
          >
            Abbrechen
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting ? "Wird erstellt …" : "Ersatzentwurf erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}
